import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { ChapterLabel, MaskedLine, Reveal } from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";

const ProjectCard = ({ item, index, onOpen }) => (
  <Reveal delay={(index % 2) * 0.1}>
    <article
      data-testid={`portfolio-card-${index + 1}`}
      className="group cursor-pointer"
      onClick={() => onOpen(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(item)}
    >
      <figure className="relative aspect-[4/5] overflow-hidden border border-noom-line bg-noom-paper">
        <img
          src={item.cover}
          alt={`${item.cardTitle} — ${item.cat}`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full transition-transform duration-[1200ms] ease-noom group-hover:scale-[1.06] ${
            item.contain ? "object-contain p-8" : "object-cover"
          }`}
        />
        <span className="absolute left-4 top-4 bg-white/90 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-noom-lilacDeep backdrop-blur-sm">
          {item.cat}
        </span>
        <span className="absolute right-4 top-4 flex h-10 w-10 translate-y-2 items-center justify-center rounded-full bg-noom-lilac text-noom-ink opacity-0 transition-all duration-500 ease-noom group-hover:translate-y-0 group-hover:opacity-100">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </figure>
      <div className="mt-5 flex items-baseline justify-between border-t border-noom-line pt-4">
        <h3 className="font-display text-lg font-semibold tracking-tight text-noom-ink transition-colors duration-300 group-hover:text-noom-lilacDeep sm:text-xl">
          {item.cardTitle}
        </h3>
        <span className="font-mono text-xs text-noom-faint">{item.meta}</span>
      </div>
    </article>
  </Reveal>
);

const Portfolio = () => {
  const { t } = useLanguage();
  const p = t.portfolio;
  const [selected, setSelected] = useState(null);
  const colA = p.items.filter((_, i) => i % 2 === 0);
  const colB = p.items.filter((_, i) => i % 2 === 1);

  return (
    <section id="portfolio" className="border-b border-noom-line bg-white" data-testid="section-portfolio">
      <div className="mx-auto max-w-[1600px] px-6 py-24 lg:px-16 lg:py-36">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <ChapterLabel testId="portfolio-label">{p.label}</ChapterLabel>
            <h2 className="mt-6 font-display text-4xl font-semibold tracking-[-0.02em] text-noom-ink sm:text-5xl">
              <MaskedLine inView>{p.heading}</MaskedLine>
            </h2>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-noom-muted" data-testid="portfolio-note">
              {p.note}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-10 md:grid-cols-2 lg:gap-14" data-testid="grid-portfolio-projects">
          <div className="flex flex-col gap-10 lg:gap-14">
            {colA.map((item) => (
              <ProjectCard key={item.id} item={item} index={p.items.indexOf(item)} onOpen={setSelected} />
            ))}
          </div>
          <div className="flex flex-col gap-10 lg:mt-28 lg:gap-14">
            {colB.map((item) => (
              <ProjectCard key={item.id} item={item} index={p.items.indexOf(item)} onOpen={setSelected} />
            ))}
          </div>
        </div>
      </div>

      <ProjectModal project={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default Portfolio;
